import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import { isDefined } from '@fabric-app/utils/guards';
import { UpdateIcon } from '@radix-ui/react-icons';

import { shallow } from 'zustand/shallow';
import BackButton from '../../../backNavigation/components/BackButton';
import { GlobalSearchIconButton } from '../../../search/components/GlobalSearchIconButton';
import useUIStore from '@/src/store/ui';
import { DashboardHeader } from '@fabric-app/business/dashboard/components/DashboardHeader';
import { ZoomSliderWithinDropdownV2 } from '@/src/components/ZoomSlider/ZoomSliderWithinDropdown';
import type { UseGetFolderIntegrationWithIntegrationReturnType } from '@fabric-app/business/folder/hooks/useGetFolderIntegration';
import { useFilterToggleButton } from '@fabric-app/business/hooks/useFilterToggleButton';
import { pick } from '@fabric-app/utils/object/manipulation';
import { ResourceUiDropdownShowControls } from '@fabric-app/business/resources/components/DropdownShowControls';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import type { ResourceDetailFolder } from '@fabric-app/types/resources';
import { useRemoveMirrorRegistry } from '@/src/modules/connections/hooks/useRemoveMirrorRegistry';
import { useResourcePermissionsByCurrentUser } from '@fabric-app/business/resource-detail/components/context/getDisabledActionsReasons';
import { useMirrorRegistryForceSync } from '@/src/modules/connections/hooks/useMirrorRegistryForceSync';
import { MirrorRegistryStatusIndicator } from '@/src/modules/connections/components/MirrorRegistryStatusBadge/MirrorRegistryStatusIndicator';
import { BadgePermissionRole } from '@fabric-app/business/permission/components/BadgePermissionRole';
import { DropdownGroupResumePauseMirrorRegistry } from '@/src/modules/connections/components/DropdownGroupResumePauseMirrorRegistry';
import { IconTrash } from '@fabric-app/icons/svg';
import { DashboardHeaderFilterSortResources } from '@fabric-app/business/dashboard/components/DashboardHeaderFilterSortResources';
import { IntegrationResourceTopBarPath } from '@/src/modules/connections/components/IntegrationResourceTopBarPath';
import { ViewModeSwitcher } from '@fabric-app/business/resources/components/ResourceList/ViewModeSwitcher/ViewModeSwitcher';
import { useResourceListPreferencesContext } from '@fabric-app/business/resources/components/ResourceList/contexts/resourceListContextPreferences';

type IntegrationViewProps = {
  overrideOnClose?: VoidFunction | null;
  folder: ResourceDetailFolder;
  folderIntegration: UseGetFolderIntegrationWithIntegrationReturnType;
  readOnly: boolean;
};

/**
 * Header view for Gdrive connection and ios screenshots
 * in teh future we might decouple these two as well but we will see
 */
export const IntegrationFolderHeader: React.FC<IntegrationViewProps> = (props) => {
  const { element: filterToggleButtonEl, ...filterVisibilityState } = useFilterToggleButton();
  const { isMobileView } = useResponsive();

  const { overrideOnClose, folderIntegration, folder, readOnly } = props;

  const { folderIntegration: integration, folderMirror } = folderIntegration;

  const removeConnectionMirror = useRemoveMirrorRegistry();

  const { userRole } = useResourcePermissionsByCurrentUser(folder);
  const { viewMode } = useResourceListPreferencesContext();

  const { zoomLevel, setZoomLevel } = useUIStore(
    (s) => pick(s, ['zoomLevel', 'setZoomLevel']),
    shallow,
  );

  const folderResync = useMirrorRegistryForceSync({
    integration,
    mirrorRegistry: folderMirror,
  });

  const folderDropdown = !readOnly && userRole === 'owner' && (
    <DropdownMenu.Base
      renderTriggerElement={({ onOpenChange }) => (
        <DropdownMenu.TriggerButtonThreeDots onOpenChange={onOpenChange} />
      )}
    >
      <DropdownGroupResumePauseMirrorRegistry mirrorRegistry={folderMirror} />

      {isDefined(folderResync) && (
        <DropdownMenu.Group>
          <DropdownMenu.Item
            onClick={folderResync}
            data-testid="folder-resync-connection"
            iconElement={<UpdateIcon />}
          >
            Resync now
          </DropdownMenu.Item>
        </DropdownMenu.Group>
      )}
      <DropdownMenu.Group>
        <DropdownMenu.Item
          onClick={() =>
            removeConnectionMirror({
              integration: folderIntegration.folderIntegration,
              mirrorRegistry: folderIntegration.folderMirror,
            })
          }
          data-testid="folder-remove-integration"
          iconElement={<IconTrash />}
          variant="danger"
        >
          Disconnect
        </DropdownMenu.Item>
      </DropdownMenu.Group>
      {!isMobileView && ['Grid', 'Masonry'].includes(viewMode) && (
        <>
          <DropdownMenu.Separator />
          <DropdownMenu.Group>
            <ResourceUiDropdownShowControls uiContext="global" />
          </DropdownMenu.Group>
        </>
      )}
      <ZoomSliderWithinDropdownV2 viewMode={viewMode} onChange={setZoomLevel} value={zoomLevel} />
    </DropdownMenu.Base>
  );

  return (
    <>
      <DashboardHeader>
        <DashboardHeader.Body>
          <DashboardHeader.Left>
            {isMobileView && overrideOnClose !== null && <BackButton onClick={overrideOnClose} />}

            <IntegrationResourceTopBarPath
              integration={folderIntegration.folderIntegration}
              resource={folder}
            />
            {folder && userRole !== 'owner' && (
              <BadgePermissionRole role={userRole} itemType="folder" />
            )}
            {folderMirror && (
              <MirrorRegistryStatusIndicator
                variant={isMobileView ? 'icon' : 'badge'}
                status={folderMirror.status}
                tooltipMessage={folderMirror.message}
              />
            )}
          </DashboardHeader.Left>
          {!isMobileView && (
            <DashboardHeader.Right>
              {filterToggleButtonEl}
              <ViewModeSwitcher />
              <GlobalSearchIconButton searchType="location" />
              {folderDropdown}
            </DashboardHeader.Right>
          )}
        </DashboardHeader.Body>
        {isMobileView && (
          <DashboardHeader.Body>
            <DashboardHeader.Left gap="elementsContainer" justifyContent="space-between">
              {filterToggleButtonEl}
              {folderDropdown}
            </DashboardHeader.Left>
          </DashboardHeader.Body>
        )}
      </DashboardHeader>
      <DashboardHeaderFilterSortResources
        visible={filterVisibilityState.value}
        resourceId={folder?.id}
      />
    </>
  );
};
